import { ApiErrorSchema, type ApiDenialReason, type ApiErrorCode } from '@clm/shared-types';

import { ApiError, ClientSuppliedScopeError } from './errors';

/**
 * One place where an HTTP failure becomes a *state the surface can render*.
 *
 * Every admin surface in CLM-37 has to tell four failures apart and say
 * something different about each: a `409` from verify-field means another
 * reviewer got there first and is not an error at all; a `404` from
 * retrieve-contract means *absent or outside your scope* and must stay
 * deliberately ambiguous (errors.ts in @clm/shared-types — a `403` there would
 * be an existence oracle over cloudEQ's client list); a `501` means the
 * authorisation path ran and passed but the behaviour behind it is unbuilt; and
 * a `ZodError` means the API answered off-contract, which is a defect to report
 * rather than data to display.
 *
 * Written once, here, because the 409 story in particular is easy to get wrong
 * by accident: caught by a generic `catch` it renders as a red toast, which
 * tells a reviewer their work failed when in fact the field is settled.
 *
 * ## Why `501` keys on the status and not on the code
 *
 * `API_ERROR_CODES` has no `not_implemented` member. `apps/api/src/rejections.ts`
 * maps the not-implemented rejection onto `code: 'internal'` with status `501`,
 * so the body cannot distinguish it from a `500`. The status can, and does.
 */
export type ApiProblemKind =
  | 'conflict'
  | 'not_found'
  /**
   * A `403` the gateway issued *after* resolving the caller's role and scope:
   * this caller may not take this action here (`reason:
   * 'action_not_permitted'`) — usually the role gate, but also a Scope-All
   * requirement or a handler's own refusal, such as cancelling another
   * person's run. Also every `403` from a gateway that predates `reason`, which
   * cannot say.
   */
  | 'forbidden'
  /**
   * A `403` at gateway step 4, where the sign-in itself resolved to nothing CLM
   * can act on — so *every* call fails, and no surface's "your role was not
   * accepted" sentence is true. See {@link forbiddenKind}.
   */
  | 'no_clm_scope_group'
  | 'no_clm_role_group'
  | 'no_groups_claim'
  /** 401 with no token or an expired one — the spec's `expired`: sign in again. */
  | 'unauthenticated'
  /**
   * CLM-65 — 401 while the attached token was still valid: the API rejected it
   * structurally (issuer, audience). Signing in again will not help.
   */
  | 'rejected'
  | 'validation'
  | 'not_implemented'
  /** The API answered, but not with the shape this build was compiled against. */
  | 'off_contract'
  /** The client refused to construct the request — a programming error. */
  | 'client_scope_refusal'
  | 'unknown';

/**
 * The `code` an {@link ApiError} body carried, when it carried a well-formed
 * one. `null` for a non-JSON body, a body from something that is not this API,
 * or an aborted request.
 */
export function apiErrorCode(error: unknown): ApiErrorCode | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = ApiErrorSchema.safeParse(error.body);
  return parsed.success ? parsed.data.code : null;
}

/**
 * The `reason` a `forbidden` body carried, or `undefined` when it carried none
 * (a gateway that predates it) or the body did not parse. A `reason` outside the
 * vocabulary this build knows fails the parse, so a newer gateway's new cause
 * reads as a plain `forbidden` (by status) rather than as somebody else's copy.
 */
export function apiDenialReason(error: unknown): ApiDenialReason | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const parsed = ApiErrorSchema.safeParse(error.body);
  return parsed.success ? parsed.data.reason : undefined;
}

/**
 * How far in the future the attached token's `exp` must sit for a 401 to read
 * as *rejected* rather than *expired*. Sixty seconds absorbs clock skew between
 * the browser and the gateway, and a token within a minute of expiry is one
 * MSAL would refresh on the next call anyway — so "sign in again" is the right
 * sentence there too.
 */
export const REJECTED_TOKEN_GRACE_SECONDS = 60;

/**
 * CLM-65 (D-65-3) — which 401 this is, from the attached token alone.
 *
 * The API's body is identical for every auth failure by design
 * (`apps/api/src/rejections.ts`), so the only local evidence is the token the
 * client itself sent. `exp` more than the grace window in the future means the
 * gateway refused a live token — a configuration mismatch (the first production
 * sign-in: v1 issuer vs the v2.0 issuer the API expects) that no amount of
 * signing in again will fix. Anything else — no token, unreadable token, expiry
 * past or imminent — keeps today's "Session expired". Decoded, not verified:
 * this picks a sentence, never a permission.
 */
export function unauthenticatedKind(
  token_exp: number | null,
  now_ms: number = Date.now(),
): 'unauthenticated' | 'rejected' {
  if (token_exp === null) return 'unauthenticated';
  return token_exp * 1000 > now_ms + REJECTED_TOKEN_GRACE_SECONDS * 1000
    ? 'rejected'
    : 'unauthenticated';
}

/**
 * Which `403` this is, from the body's `reason` (30 Sep 2026).
 *
 * That day a member of CEQ-CLM-Admin who held no CEQ-CLM-Scope-* group was
 * refused on every call, and this page told her *"Your role may not take this
 * action"*. Her role was fine. The gateway knew the cause and kept it in its
 * log; it now also sends it, as a fixed code (`apps/api/src/rejections.ts`,
 * rule 1).
 *
 * The three sign-in causes become kinds of their own rather than a detail on
 * `forbidden`, and that is deliberate. Some twenty surfaces add copy to a
 * `forbidden` — *"Your sign-in was accepted; your role was not"*, *"needs the
 * Legal reviewer role"* — or switch their write controls off on one. Every one
 * of those presumes the role gate, and every one is false for a caller with no
 * scope group. A distinct kind cannot match those branches, so they fall
 * through to `ErrorNotice`'s copy for the real cause without the surface having
 * to check a reason first — provided it branches on the *kind*. A surface that
 * branches on the HTTP status instead does match a sign-in denial, and has to
 * ask {@link isSignInDenial} before printing its own role sentence (the Ingest
 * planner's `fail`, planner-actions.ts).
 *
 * `action_not_permitted`, and an absent `reason` (a gateway that predates it),
 * stay `forbidden`. Today's role copy is true for the usual case of the first,
 * the role gate, but not for its Scope-All or ownership refusals, which the
 * wire does not yet tell apart (root `architecture.md`, D-123-1). For the second
 * it is the best the page can say.
 */
export function forbiddenKind(reason: ApiDenialReason | undefined): ApiProblemKind {
  switch (reason) {
    case 'no_clm_scope_group':
    case 'no_clm_role_group':
    case 'no_groups_claim':
      return reason;
    case 'action_not_permitted':
    case undefined:
      return 'forbidden';
  }
}

/** The three kinds that are the sign-in itself, not this action — {@link forbiddenKind}. */
export type SignInDenialKind = Extract<ApiProblemKind, ApiDenialReason>;

/**
 * Whether a classified failure is one of the sign-in denials, for which no
 * surface's role sentence is true: every call fails the same way until the
 * caller's groups change.
 */
export function isSignInDenial(kind: ApiProblemKind): kind is SignInDenialKind {
  return kind === 'no_clm_scope_group' || kind === 'no_clm_role_group' || kind === 'no_groups_claim';
}

export function classifyApiError(error: unknown): ApiProblemKind {
  if (error instanceof ClientSuppliedScopeError) return 'client_scope_refusal';

  // A ZodError from a response `.parse` — the wrapper validated the payload and
  // it did not match the contract. Matched by name rather than by `instanceof`
  // so the app does not have to import zod to recognise one.
  if (error instanceof Error && error.name === 'ZodError') return 'off_contract';

  if (!(error instanceof ApiError)) return 'unknown';

  // Status first for 501: see the module note.
  if (error.status === 501) return 'not_implemented';

  const code = apiErrorCode(error);
  if (code === 'conflict') return 'conflict';
  if (code === 'not_found') return 'not_found';
  if (code === 'forbidden') return forbiddenKind(apiDenialReason(error));
  if (code === 'unauthenticated') return unauthenticatedKind(error.token_exp);
  if (code === 'validation_failed') return 'validation';

  // No parseable body — fall back to the status, which every proxy preserves.
  switch (error.status) {
    case 401:
      return unauthenticatedKind(error.token_exp);
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    // 400 is what `apps/api/src/rejections.ts` actually sends for
    // `validation_failed`; 422 is accepted too so a proxy or a future revision
    // that prefers it does not fall through to `unknown`.
    case 400:
    case 422:
      return 'validation';
    default:
      return 'unknown';
  }
}
